import {CalendarClock, Clock, MapPin} from "lucide-react";
import {Link} from "react-router-dom";

import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import {formatTime, moment} from "@/src/modules/core/lib";
import {ShiftPropertyDisplayBadges} from "@/src/modules/shifts/components/shift-property-display-badges";
import {Shift} from "@/src/modules/shifts/types";

/** After hero "next shift" only (`slice(1)`). Full list while an active shift is shown. */
export type UpcomingShiftsListScope = "after-next" | "full";

interface UpcomingShiftsProps {
  upcomingShifts: Shift[];
  listScope: UpcomingShiftsListScope;
  compact?: boolean;
}

export function UpcomingShifts({upcomingShifts, listScope, compact = false}: UpcomingShiftsProps) {
  const copy =
    listScope === "after-next"
      ? {
          emptyTitle: "No other shifts",
          emptyDescription:
            "You're not scheduled for anything after this shift. You're all set once it ends.",
          listTitle: "Later shifts",
          listDescription: "Scheduled after the one above.",
        }
      : {
          emptyTitle: "No upcoming shifts",
          emptyDescription: "Nothing else scheduled at the moment.",
          listTitle: "Upcoming shifts",
          listDescription: "Other shifts on your schedule.",
        };

  if (!upcomingShifts.length) {
    return (
      <Card className={compact ? "border-dashed shadow-none" : "h-full border-2 border-dashed shadow-none"}>
        <CardContent
          className={compact ? "flex flex-col items-center justify-center py-8 text-center" : "flex flex-col items-center justify-center py-16 text-center"}
        >
          <div className={compact ? "bg-muted mb-3 rounded-full p-4" : "rounded-full bg-muted p-6 mb-4"}>
            <CalendarClock className={compact ? "text-muted-foreground size-6" : "size-8 text-muted-foreground"} />
          </div>
          <h3 className={compact ? "mb-1 text-base font-semibold" : "text-xl font-semibold mb-2"}>{copy.emptyTitle}</h3>

          <p className="text-sm text-muted-foreground max-w-sm">{copy.emptyDescription}</p>
          <div>
            <Button variant="link" render={<Link to="/shifts-history" />}>
              View shift history
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <div>{copy.listTitle}</div>
          <div>
            <Button variant="link" render={<Link to="/shifts-history" />}>
              View shift history
            </Button>
          </div>
        </CardTitle>
        <CardDescription>{copy.listDescription}</CardDescription>
      </CardHeader>
      <CardContent className="min-w-0">
        <div className="flex min-w-0 flex-col gap-3">
          {upcomingShifts.map((shift) => (
            <ShiftItem key={`shift-${shift.shift_id}`} shift={shift} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

interface ShiftItem {
  shift: Shift;
}

function ShiftItem({shift}: ShiftItem) {
  const fromDate = moment(shift.from_datetime);

  return (
    <div className="flex min-w-0 gap-4 rounded-lg border p-4">
      <div className="flex size-16 shrink-0 flex-col items-center justify-center rounded-lg bg-muted">
        <p className="text-xs font-medium text-muted-foreground">{fromDate.format("D")}</p>
        <p className="text-lg font-bold">{fromDate.format("MMM")}</p>
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <p className="font-semibold">Shift #{shift.shift_id}</p>
          <Badge variant="outline" className="text-xs">
            {shift.type}
          </Badge>
        </div>
        <div className="flex flex-col gap-2 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="size-3.5 shrink-0" />
            {formatTime(shift.from_datetime)} - {formatTime(shift.to_datetime)}
          </span>
          <div className="flex min-w-0 items-start gap-1.5">
            <MapPin className="mt-0.5 size-3.5 shrink-0" />
            {shift.properties.length > 0 ? (
              <ShiftPropertyDisplayBadges
                properties={shift.properties}
                className="min-w-0 flex-1"
                badgeClassName="text-xs"
              />
            ) : (
              <span>—</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
